import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserCircle, Users, Type, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet } from '../../api/client';
import { useCreatePerformance, useUpdatePerformance } from '../../api/usePerformances';
import { usePermissions } from '../../hooks/usePermission';
import type { Performance, ExternalPhotographer, PerformanceService as PerfService } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatCurrency } from '../../utils/format';

type AssigneeType = 'collaboratore' | 'utente' | 'altro';

interface DraftAssignment {
  type: AssigneeType;
  collaboratorId?: string;
  userId?: string;
  role: string;
  fee?: string;
}

interface SavedAssignment {
  collaboratorId?: string;
  userId?: string;
  role: string;
  fee?: number;
  _displayName: string;
}

interface Props {
  edit: Performance | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const SELECT_STYLE: React.CSSProperties = {
  width: '100%', background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
  color: 'var(--color-text)', fontSize: 'var(--text-sm)',
  padding: '0.5rem 0.75rem', outline: 'none', boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
  fontWeight: 600, display: 'block', marginBottom: '0.375rem',
};

export function PerformanceForm({ edit, onSuccess, onCancel }: Props) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('performances.manage');

  const [title, setTitle] = useState(edit?.title ?? '');
  const [eventDate, setEventDate] = useState(edit?.eventDate?.split('T')[0] ?? '');
  const [location, setLocation] = useState(edit?.location ?? '');
  const [extId, setExtId] = useState(edit?.externalPhotographerId ?? '');
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(
    () => new Set(edit?.serviceItems?.map(si => si.performanceServiceId) ?? [])
  );
  const [notes, setNotes] = useState(edit?.notes ?? '');
  const [assignments, setAssignments] = useState<SavedAssignment[]>(() => {
    if (!edit?.assignments) return [];
    return edit.assignments.map(a => ({
      collaboratorId: a.collaboratorId ?? undefined,
      userId: a.userId ?? undefined,
      role: a.role,
      fee: a.fee ?? undefined,
      _displayName: a.collaborator?.name ?? a.user?.name ?? a.role,
    }));
  });
  const [draft, setDraft] = useState<DraftAssignment | null>(null);

  const { data: externalsRaw } = useQuery<ExternalPhotographer[] | { data: ExternalPhotographer[] }>({
    queryKey: ['external-photographers', '', false, 1],
    queryFn: () => apiGet('/external-photographers?limit=100'),
  });
  const { data: servicesData } = useQuery<PerfService[]>({
    queryKey: ['performance-services'],
    queryFn: () => apiGet<PerfService[]>('/performances/services'),
  });
  const { data: collabsRaw } = useQuery<{ data: { id: string; name: string; role?: string }[] } | { id: string; name: string; role?: string }[]>({
    queryKey: ['collaborators', '', 'true', 1],
    queryFn: () => apiGet('/collaborators?limit=100&active=true'),
  });
  const { data: usersRaw } = useQuery<{ data: { id: string; name: string; email: string }[] } | { id: string; name: string; email: string }[]>({
    queryKey: ['users', 'list'],
    queryFn: () => apiGet('/users'),
    enabled: canManage,
  });

  const externals: ExternalPhotographer[] = Array.isArray(externalsRaw)
    ? externalsRaw
    : (externalsRaw as { data: ExternalPhotographer[] })?.data ?? [];
  const services = servicesData ?? [];
  const activeServices = useMemo(() => services.filter(s => s.isActive), [services]);
  const collabs: { id: string; name: string; role?: string }[] = Array.isArray(collabsRaw)
    ? collabsRaw
    : (collabsRaw as { data: { id: string; name: string; role?: string }[] })?.data ?? [];
  const users: { id: string; name: string; email: string }[] = Array.isArray(usersRaw)
    ? usersRaw
    : (usersRaw as { data: { id: string; name: string; email: string }[] })?.data ?? [];

  const createMutation = useCreatePerformance();
  const updateMutation = useUpdatePerformance();

  const toggleService = (serviceId: string) => {
    const next = new Set(selectedServiceIds);
    if (next.has(serviceId)) next.delete(serviceId); else next.add(serviceId);
    setSelectedServiceIds(next);
  };

  const toggleAllServices = () => {
    if (selectedServiceIds.size === activeServices.length) {
      setSelectedServiceIds(new Set());
    } else {
      setSelectedServiceIds(new Set(activeServices.map(s => s.id)));
    }
  };

  const openDraft = () => {
    setDraft({ type: 'collaboratore', role: '' });
  };

  const closeDraft = () => setDraft(null);

  const commitDraft = () => {
    if (!draft) return;
    if (!draft.role.trim() && draft.type !== 'collaboratore' && draft.type !== 'utente') {
      toast.error('Ruolo obbligatorio per tipo "Altro"');
      return;
    }

    let displayName = 'N/D';
    let finalRole = draft.role;

    if (draft.type === 'collaboratore') {
      if (!draft.collaboratorId) { toast.error('Seleziona un collaboratore'); return; }
      const c = collabs.find(x => x.id === draft.collaboratorId);
      displayName = c?.name ?? 'Collaboratore';
      if (!finalRole.trim()) finalRole = c?.role ?? '';
    } else if (draft.type === 'utente') {
      if (!draft.userId) { toast.error('Seleziona un utente'); return; }
      const u = users.find(x => x.id === draft.userId);
      displayName = u?.name ?? 'Utente';
      if (!finalRole.trim()) finalRole = 'Fotografo';
    } else {
      if (!finalRole.trim()) { toast.error('Ruolo obbligatorio'); return; }
      displayName = finalRole;
    }

    const saved: SavedAssignment = {
      collaboratorId: draft.type === 'collaboratore' ? draft.collaboratorId : undefined,
      userId: draft.type === 'utente' ? draft.userId : undefined,
      role: finalRole,
      fee: draft.fee ? Number(draft.fee) : undefined,
      _displayName: displayName,
    };
    setAssignments(prev => [...prev, saved]);
    setDraft(null);
  };

  const removeAssignment = (i: number) => {
    setAssignments(prev => prev.filter((_, j) => j !== i));
  };

  const assigneeTypeOptions: { value: AssigneeType; label: string; icon: React.ReactNode }[] = [
    { value: 'collaboratore', label: 'Collaboratore', icon: <UserCircle size={13} /> },
    { value: 'altro', label: 'Altro', icon: <Type size={13} /> },
    ...(canManage ? [{ value: 'utente' as AssigneeType, label: 'Utente sistema', icon: <Users size={13} /> }] : []),
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventDate || !extId || selectedServiceIds.size === 0) {
      toast.error('Data, Esterno e almeno un Servizio sono obbligatori');
      return;
    }

    const body = {
      title: title || undefined,
      eventDate: new Date(eventDate).toISOString(),
      location: location || undefined,
      externalPhotographerId: extId,
      serviceIds: [...selectedServiceIds],
      notes: notes || undefined,
      assignments: assignments.length > 0
        ? assignments.map(({ _displayName: _d, fee, ...rest }) => ({ ...rest, fee }))
        : undefined,
    };

    if (edit) {
      // For edit, convert to serviceItems format for PATCH
      const editBody = {
        id: edit.id,
        title: title || undefined,
        eventDate: body.eventDate,
        location: location || undefined,
        serviceItems: body.serviceIds.map(sid => ({ performanceServiceId: sid })),
        notes: notes || undefined,
        status: edit.status !== 'ANNULLATO' ? undefined : undefined, // preserve status
        assignments: body.assignments,
      };
      updateMutation.mutate(editBody, {
        onSuccess: () => { toast.success('Prestazione aggiornata'); onSuccess(); },
        onError: (e: Error) => toast.error(e.message),
      });
    } else {
      createMutation.mutate(body, {
        onSuccess: () => { toast.success('Prestazione creata'); onSuccess(); },
        onError: (e: Error) => toast.error(e.message),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Input label="Titolo (opzionale)" value={title} onChange={e => setTitle(e.target.value)} placeholder="Auto-generato se vuoto" />
      <Input label="Data evento *" type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} required />

      <div>
        <label style={LABEL_STYLE}>Esterno *</label>
        <select value={extId} onChange={e => setExtId(e.target.value)} required style={SELECT_STYLE}>
          <option value="">Seleziona esterno…</option>
          {externals.map((ext: ExternalPhotographer) => (
            <option key={ext.id} value={ext.id}>
              {ext.studioName ?? `${ext.firstName} ${ext.lastName}`}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label style={LABEL_STYLE}>
          Servizi * ({selectedServiceIds.size} selezionati)
        </label>
        <div style={{
          maxHeight: 220, overflowY: 'auto',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
          padding: '0.5rem', background: 'var(--glass-bg-card)',
        }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.375rem 0.5rem', fontSize: 'var(--text-sm)', fontWeight: 600,
            color: 'var(--color-text)', cursor: 'pointer',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            marginBottom: '0.25rem',
          }}>
            <input
              type="checkbox"
              checked={selectedServiceIds.size === activeServices.length && activeServices.length > 0}
              ref={el => { if (el && activeServices.length > 0) el.indeterminate = selectedServiceIds.size > 0 && selectedServiceIds.size < activeServices.length; }}
              onChange={toggleAllServices}
              style={{ accentColor: 'var(--color-primary)' }}
            />
            Seleziona tutti
          </label>
          {activeServices.map(s => (
            <label key={s.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '0.5rem', padding: '0.375rem 0.5rem',
              fontSize: 'var(--text-sm)', cursor: 'pointer',
              borderRadius: 'var(--radius-interactive)',
              background: selectedServiceIds.has(s.id) ? 'var(--color-primary-highlight)' : 'transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={selectedServiceIds.has(s.id)}
                  onChange={() => toggleService(s.id)}
                  style={{ accentColor: 'var(--color-primary)' }}
                />
                <span>{s.name}</span>
              </div>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(s.basePrice)}
              </span>
            </label>
          ))}
        </div>
      </div>

      <Input label="Luogo" value={location} onChange={e => setLocation(e.target.value)} />

      {/* Assegnazioni */}
      <div>
        <label style={{ fontSize: 'var(--text-sm)', fontWeight: 600, display: 'block', marginBottom: '0.5rem', color: 'var(--color-text)' }}>
          Assegnazioni
        </label>

        {/* Righe salvate */}
        {assignments.map((a, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem',
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', padding: '0.625rem 0.75rem',
            marginBottom: '0.375rem',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)', margin: 0 }}>
                {a._displayName}
                {a.role && a.role !== a._displayName && (
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}> · {a.role}</span>
                )}
              </p>
              {a.fee != null && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                  {formatCurrency(a.fee)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => removeAssignment(i)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', lineHeight: 1 }}
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {/* Riga edit inline */}
        {draft && (
          <div style={{
            background: 'var(--glass-bg-card)', border: '1px solid var(--color-primary)',
            borderRadius: 'var(--radius-card)', padding: '0.75rem',
            display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.5rem',
          }}>
            {/* Tipo */}
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              {assigneeTypeOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDraft(d => d ? { ...d, type: opt.value, collaboratorId: undefined, userId: undefined } : d)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.25rem 0.625rem', border: '1px solid',
                    borderColor: draft.type === opt.value ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-interactive)', background: draft.type === opt.value ? 'var(--color-primary-highlight)' : 'var(--glass-bg-card)',
                    color: draft.type === opt.value ? 'var(--color-primary)' : 'var(--color-text-muted)',
                    fontSize: 'var(--text-xs)', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>

            {/* Assegnatario */}
            {draft.type === 'collaboratore' && (
              <select
                value={draft.collaboratorId ?? ''}
                onChange={e => {
                  const c = collabs.find(x => x.id === e.target.value);
                  setDraft(d => d ? { ...d, collaboratorId: e.target.value, role: d.role || c?.role || '' } : d);
                }}
                style={SELECT_STYLE}
              >
                <option value="">Seleziona collaboratore…</option>
                {collabs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {draft.type === 'utente' && canManage && (
              <select
                value={draft.userId ?? ''}
                onChange={e => setDraft(d => d ? { ...d, userId: e.target.value } : d)}
                style={SELECT_STYLE}
              >
                <option value="">Seleziona utente…</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Ruolo"
                value={draft.role}
                onChange={e => setDraft(d => d ? { ...d, role: e.target.value } : d)}
                style={{ ...SELECT_STYLE, padding: '0.4rem 0.625rem' }}
              />
              <input
                type="number"
                placeholder="Fee €"
                min="0"
                step="0.01"
                value={draft.fee ?? ''}
                onChange={e => setDraft(d => d ? { ...d, fee: e.target.value } : d)}
                style={{ ...SELECT_STYLE, width: 90, padding: '0.4rem 0.625rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
              <Button type="button" variant="ghost" size="sm" onClick={closeDraft}>✕ Annulla</Button>
              <Button type="button" size="sm" onClick={commitDraft}>✓ Aggiungi</Button>
            </div>
          </div>
        )}

        {!draft && (
          <Button type="button" variant="secondary" size="sm" onClick={openDraft} style={{ marginTop: '0.25rem' }}>
            + Aggiungi assegnazione
          </Button>
        )}
      </div>

      <div>
        <label style={LABEL_STYLE}>Note</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          style={{
            width: '100%', background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
            color: 'var(--color-text)', fontSize: 'var(--text-sm)',
            padding: '0.5rem 0.75rem', resize: 'vertical', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
        <Button variant="ghost" type="button" onClick={onCancel}>Annulla</Button>
        <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
          {edit ? 'Salva' : 'Crea prestazione'}
        </Button>
      </div>
    </form>
  );
}
